/**
 * Web Vitals + page-load capture (RUM), dependency-free via PerformanceObserver.
 * Collects LCP, CLS, INP (best-effort), FCP, TTFB and the page-load transaction, then
 * reports once on page hide (sendBeacon) to /api/perf/ingest.
 *
 * Thresholds follow web.dev's "good / needs-improvement / poor" guidance.
 */
import { maskUrl } from '@aris/request-watcher-shared';
import type { ResolvedConfig } from './config.js';
import { uuid } from './util.js';
import { sendPerfEvents } from './perfsend.js';

const THRESHOLDS: Record<string, [number, number]> = {
  LCP: [2500, 4000],
  INP: [200, 500],
  CLS: [0.1, 0.25],
  FCP: [1800, 3000],
  TTFB: [800, 1800],
};

function rate(name: string, v: number): 'good' | 'needs-improvement' | 'poor' {
  const [g, p] = THRESHOLDS[name] ?? [Infinity, Infinity];
  return v <= g ? 'good' : v <= p ? 'needs-improvement' : 'poor';
}

export function installWebVitals(cfg: ResolvedConfig): () => void {
  if (typeof window === 'undefined' || typeof PerformanceObserver === 'undefined') return () => {};

  let lcp = 0;
  let cls = 0;
  let inp = 0;
  let fcp = 0;
  let ttfb = 0;
  let sent = false;
  const observers: PerformanceObserver[] = [];

  try {
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    if (nav) ttfb = Math.max(0, nav.responseStart - nav.startTime);
  } catch { /* unsupported */ }

  const observe = (type: string, cb: (l: PerformanceObserverEntryList) => void, opts?: PerformanceObserverInit) => {
    try {
      const o = new PerformanceObserver(cb);
      o.observe({ type, buffered: true, ...opts } as PerformanceObserverInit);
      observers.push(o);
    } catch { /* type unsupported in this browser */ }
  };

  observe('paint', (l) => {
    for (const e of l.getEntries()) if (e.name === 'first-contentful-paint') fcp = e.startTime;
  });
  observe('largest-contentful-paint', (l) => {
    const es = l.getEntries();
    const last = es[es.length - 1];
    if (last) lcp = last.startTime;
  });
  observe('layout-shift', (l) => {
    for (const e of l.getEntries() as unknown as Array<{ value: number; hadRecentInput: boolean }>) {
      if (!e.hadRecentInput) cls += e.value;
    }
  });
  observe(
    'event',
    (l) => {
      for (const e of l.getEntries() as unknown as Array<{ interactionId?: number; duration: number }>) {
        if (e.interactionId) inp = Math.max(inp, e.duration);
      }
    },
    { durationThreshold: 40 } as PerformanceObserverInit,
  );

  const resolveUser = (): string | null => {
    const u = cfg.userId;
    return (typeof u === 'function' ? u() : u) ?? null;
  };

  const finalize = () => {
    if (sent) return;
    sent = true;
    const route = location.pathname;
    const url = maskUrl(location.href, { maskPii: cfg.maskPii });
    const ts = new Date().toISOString();
    const events: Record<string, unknown>[] = [];
    const add = (kind: 'web-vital' | 'transaction', name: string, value: number) => {
      events.push({
        id: uuid(), project_id: cfg.projectId, environment: cfg.environment,
        kind, name, value: Math.round(value * 1000) / 1000,
        rating: kind === 'web-vital' ? rate(name, value) : null,
        route, url, user_id: resolveUser(), session_id: cfg.sessionId ?? null, timestamp: ts,
      });
    };

    if (lcp) add('web-vital', 'LCP', lcp);
    add('web-vital', 'CLS', cls); // CLS=0 is still a valid (good) sample
    if (inp) add('web-vital', 'INP', inp);
    if (fcp) add('web-vital', 'FCP', fcp);
    if (ttfb) add('web-vital', 'TTFB', ttfb);
    try {
      const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
      if (nav?.loadEventEnd) add('transaction', 'pageload', nav.loadEventEnd - nav.startTime);
    } catch { /* ignore */ }

    sendPerfEvents(cfg, events, true);
  };

  const onHidden = () => { if (document.visibilityState === 'hidden') finalize(); };
  document.addEventListener('visibilitychange', onHidden);
  window.addEventListener('pagehide', finalize);

  return () => {
    observers.forEach((o) => o.disconnect());
    document.removeEventListener('visibilitychange', onHidden);
    window.removeEventListener('pagehide', finalize);
  };
}
